import type { ColorScheme } from './ColorScheme';
import type { IPalette } from '@fluentui/style-utilities';
import Color from 'color';

const SHADE_VALUES = {
    calDarker: -44,
    calDark: -24,
    calDarkAlt: -10,
    calPrimary: 0,
    calSecondary: 12,
    calTertiary: 40,
    calLight: 70,
    calLighter: 84,
    calLighterAlt: 96,
};

/**
 * We dynamically generate a color scheme given a primary color value by taking the primary color and shading it with a % of black and % of white
 *
 * The percentages of white and black come from SHADE_VALUES, and create a ColorScheme where the given primary color corresponds to calPrimary, with each darker
 * shade mixing extra black and each lighter shade mixing extra white.
 *
 * Code from universal color generation is here:
 * https://msasg.visualstudio.com/_search?type=code&lp=custom-Collection&text=CalendarColorShadeGenerator&result=DefaultCollection%2FOfficeSourceDepot%2Fdevmain%2Fdevmain%2Fhxcalendarui%2Fxaml%2FSupport%2FcolorShadeGenerator%2FCalendarColorShadeGenerator.cpp&preview=1&_a=search
 *
 * @param primaryHexColor the string value of the color to use as primary
 * @param isDarkMode true if dark mode
 */
export function generateColorScheme(primaryHexColor: string, isDarkMode: boolean): ColorScheme {
    const primaryColor = Color(primaryHexColor);
    const colorScheme: ColorScheme = {
        text: '',
        textAlt: '',
        accessibleText: {
            calPrimaryBackground: '',
            calLighterBackground: '',
            msColorWhiteBackground: '',
            msColorNeutralLighterBackground: '',
        },
        calDarker: shadeColor(
            primaryColor,
            isDarkMode ? SHADE_VALUES.calLighterAlt : SHADE_VALUES.calDarker
        ).hex(),
        calDark: shadeColor(
            primaryColor,
            isDarkMode ? SHADE_VALUES.calLighter : SHADE_VALUES.calDark
        ).hex(),
        calDarkAlt: shadeColor(
            primaryColor,
            isDarkMode ? SHADE_VALUES.calLight : SHADE_VALUES.calDarkAlt
        ).hex(),
        calPrimary: primaryColor.hex(),
        calSecondary: shadeColor(
            primaryColor,
            isDarkMode ? -SHADE_VALUES.calSecondary : SHADE_VALUES.calSecondary
        ).hex(),
        calTertiary: shadeColor(
            primaryColor,
            isDarkMode ? -SHADE_VALUES.calTertiary : SHADE_VALUES.calTertiary
        ).hex(),
        calLight: shadeColor(
            primaryColor,
            isDarkMode ? SHADE_VALUES.calDarkAlt : SHADE_VALUES.calLight
        ).hex(),
        calLighter: shadeColor(
            primaryColor,
            isDarkMode ? SHADE_VALUES.calDark : SHADE_VALUES.calLighter
        ).hex(),
        calLighterAlt: shadeColor(
            primaryColor,
            isDarkMode ? SHADE_VALUES.calDarker : SHADE_VALUES.calLighterAlt
        ).hex(),
        colorName: () => '',
    };

    return colorScheme;
}

/**
 * Create a partial Fluent Palette with theme colors from color scheme
 * @param scheme ColorScheme object
 * @return the partial theme palette based on this color scheme
 */
export function generateThemePalette(scheme: ColorScheme): Partial<IPalette> {
    return {
        themePrimary: scheme.calPrimary,
        themeLighterAlt: scheme.calLighterAlt,
        themeLighter: scheme.calLighter,
        themeLight: scheme.calLight,
        themeTertiary: scheme.calTertiary,
        themeSecondary: scheme.calSecondary,
        themeDarkAlt: scheme.calDarkAlt,
        themeDark: scheme.calDark,
        themeDarker: scheme.calDarker,
    };
}

/**
 * Shades in black or white into the given RGB value and returns a new value.
 * @param color the Color to shade
 * @param amount the amount to shade, [-100, 100]. Negative shades in black, positive shades in white i.e. -100 returns black.
 */
function shadeColor(color: Color, amount: number): Color {
    let newColor = Color(color.rgb());
    if (amount == 0) {
        return newColor;
    } else if (amount < 0) {
        // Shade by mixing with [amount]% black.  Black is at V = 0, for any H and S values.
        newColor = newColor.value(color.value() * (1 + amount / 100.0));
    } else {
        // Tint by mixing with [amount]% white.  White is the point where V = 1 and S = 0 for any H value.
        newColor = newColor.value(color.value() + ((100 - color.value()) * amount) / 100.0);
        newColor = newColor.saturationv(color.saturationv() * (1 - amount / 100));
    }

    newColor = newColor.saturationv(clamp(newColor.saturationv()));
    newColor = newColor.value(clamp(newColor.value()));
    return newColor;
}

function clamp(value: number): number {
    if (value < 0) {
        return 0;
    }
    if (value > 100) {
        return 100;
    }

    return value;
}
